import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle2, RefreshCw } from 'lucide-react';
import { onboardingService } from '@/services/onboardingService';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';
import { n8nWorkflowUpdateService } from '@/services/n8nWorkflowUpdateService';
import { n8nWorkflowsService, N8nWorkflow } from '@/services/n8nWorkflowsService';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from 'lucide-react';
import { n8nProxyService } from '@/services/n8nProxyService';

interface CompletionStepProps {
  onNext: (data: Record<string, any>) => void;
  data?: Record<string, any>;
  isLastStep?: boolean;
}

export const CompletionStep: React.FC<CompletionStepProps> = ({ onNext, data = {} }) => {
  const { toast } = useToast();
  const [isResetting, setIsResetting] = React.useState(false);
  const [isUpdatingWorkflow, setIsUpdatingWorkflow] = React.useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isLoadingWorkflows, setIsLoadingWorkflows] = useState(false);
  const [workflows, setWorkflows] = useState<N8nWorkflow[]>([]);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Carregar workflows quando o diálogo for aberto
  const loadWorkflows = async () => {
    setIsLoadingWorkflows(true);
    setErrorMessage(null);
    
    try {
      const workflowsList = await n8nWorkflowsService.getWorkflows({
        forceRefresh: true
      });
      
      setWorkflows(workflowsList);
      
      if (workflowsList.length > 0) {
        setSelectedWorkflowId(String(workflowsList[0].id));
      } else {
        setErrorMessage("Nenhum workflow encontrado. Por favor, verifique se você possui workflows criados no n8n.");
      }
    } catch (error) {
      console.error('Erro ao carregar workflows:', error);
      setErrorMessage(`Não foi possível carregar os workflows: ${error.message}`);
    } finally {
      setIsLoadingWorkflows(false);
    }
  };

  const handleComplete = () => {
    onNext({ 
      completed: true,
      completion_date: new Date().toISOString()
    });
  };

  const handleResetOnboarding = async () => {
    try {
      setIsResetting(true);
      const session = await supabase.auth.getSession();
      if (!session.data.session?.user.id) {
        throw new Error('Usuário não autenticado');
      }

      await onboardingService.resetOnboarding(session.data.session.user.id);
      
      toast({
        title: "Onboarding reiniciado",
        description: "O processo de configuração foi reiniciado com sucesso.",
        variant: "default",
      });
      
      // Recarregar a página para reiniciar o onboarding
      window.location.href = '/dashboard/onboarding';
    } catch (error) {
      console.error('Erro ao reiniciar onboarding:', error);
      toast({
        title: "Erro ao reiniciar",
        description: "Não foi possível reiniciar o processo de configuração.",
        variant: "destructive",
      });
    } finally {
      setIsResetting(false);
    }
  };

  const handleOpenWorkflowDialog = async () => {
    setIsDialogOpen(true);
    await loadWorkflows();
  };

  const handleCloseWorkflowDialog = () => {
    setIsDialogOpen(false);
    setSelectedWorkflowId("");
  };

  const handleUpdateWorkflow = async () => {
    if (!selectedWorkflowId) {
      setErrorMessage("Por favor, selecione um workflow.");
      return;
    }

    setIsUpdatingWorkflow(true);
    setErrorMessage(null);

    try {
      // Verificar a conexão com n8n antes de prosseguir
      console.log("Verificando conexão com n8n antes de atualizar o workflow...");
      try {
        const connectionStatus = await n8nProxyService.testConnection(true);
        if (!connectionStatus.connected) {
          throw new Error("Não foi possível conectar ao serviço n8n. Verifique sua conexão e tente novamente.");
        }
        console.log("Conexão com n8n verificada com sucesso.");
      } catch (connectionError) {
        console.error("Erro ao verificar conexão com n8n:", connectionError);
        throw new Error("Não foi possível conectar ao serviço de automação. Verifique sua conexão e tente novamente.");
      }

      const session = await supabase.auth.getSession();
      if (!session.data.session?.user.id) {
        throw new Error('Usuário não autenticado');
      }

      // Obter os dados do onboarding
      const onboardingState = await onboardingService.getOnboardingState(session.data.session.user.id);
      if (!onboardingState) {
        throw new Error('Dados do onboarding não encontrados');
      }
      
      // Formatar os dados para a atualização do workflow
      const onboardingData = {
        business: onboardingState.steps_data?.business_info || {},
        whatsapp: onboardingState.steps_data?.whatsapp || {},
        ai_agent: onboardingState.steps_data?.ai_agent || {},
        calendar: onboardingState.steps_data?.calendar || {},
        content: onboardingState.steps_data?.content || {}
      };
      
      // Primeiro, tentar criar um workflow automaticamente se necessário
      if (workflows.length === 0) {
        try {
          // Mostrar mensagem de que estamos criando um workflow
          toast({
            title: "Criando um workspace",
            description: "Estamos provisionando o ambiente n8n para você automaticamente.",
          });
          
          console.log("Chamando n8n-provisioner para criar workspace e workflow...");
          
          // Obter a sessão atual para o token de autenticação
          const session = await supabase.auth.getSession();
          if (!session.data.session?.access_token) {
            throw new Error("Sessão não disponível. Por favor, faça login novamente.");
          }
          
          // Chamar a função para provisionar o n8n (workspace e workflow)
          const { data: provisionData, error: provisionError } = await supabase.functions.invoke('n8n-provisioner', {
            body: { 
              action: "provision",
              userId: session.data.session.user.id
            },
            headers: {
              Authorization: `Bearer ${session.data.session.access_token}`
            }
          });
          
          if (provisionError) {
            throw new Error(`Erro ao provisionar n8n: ${provisionError.message}`);
          }
          
          if (!provisionData?.success || !provisionData?.workspace) {
            throw new Error('Resposta inválida do serviço de provisionamento');
          }
          
          // Verificar se este é um workspace existente ou um novo
          if (provisionData.workspace.isExisting) {
            console.log("Workspace já existia. Carregando os workflows...");
            await loadWorkflows();
          } else {
            console.log("Workspace n8n provisionado com sucesso:", provisionData.workspace);
            
            // Atualizar estado dos workflows e o workflow selecionado
            setWorkflows([{
              id: provisionData.workspace.workflow_id,
              name: provisionData.workspace.workflow_name || "ZapCore Workflow",
              active: false,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            }]);
            
            setSelectedWorkflowId(provisionData.workspace.workflow_id);
            
            // Atualizar a mensagem para informar o usuário
            toast({
              title: "Ambiente provisionado com sucesso",
              description: "Seu workspace e workflow n8n foram criados automaticamente.",
            });
          }
        } catch (createErr) {
          console.error("Erro ao provisionar n8n:", createErr);
          setErrorMessage(`Não conseguimos provisionar seu ambiente n8n. Erro: ${createErr.message}`);
          // Não retornamos aqui, já que vamos tentar usar o workflow selecionado (se existir)
        }
      }
      
      // Verificar novamente se agora temos um workflow selecionado
      if (!selectedWorkflowId) {
        throw new Error('Nenhum workflow selecionado. Tente novamente.');
      }
      
      // Agora, atualizar o workflow com os dados do onboarding
      const result = await n8nWorkflowUpdateService.updateWorkflowWithOnboardingData(
        selectedWorkflowId,
        onboardingData,
        session.data.session.user.id
      );
      
      if (result.success) {
        toast({
          title: "Workflow configurado",
          description: "O workflow foi atualizado com sucesso com seus dados de configuração.",
          variant: "default",
        });
        
        // Aguardar um momento para garantir que as operações assíncronas do backend tenham tempo de finalizar
        setTimeout(() => {
          // Fechar o diálogo
          setIsDialogOpen(false);
          
          // Avançar para a próxima etapa
          onNext({
            completion: {
              completed: true,
              workflow_id: selectedWorkflowId,
              timestamp: new Date().toISOString()
            }
          });
        }, 1500);
      } else {
        throw new Error(result.message || 'Falha ao atualizar o workflow');
      }
    } catch (error) {
      console.error('Erro ao atualizar workflow:', error);
      setErrorMessage(`Erro ao atualizar workflow: ${error.message}`);
      toast({
        title: "Erro ao atualizar workflow",
        description: error.message || "Não foi possível atualizar o workflow com seus dados.",
        variant: "destructive",
      });
    } finally {
      setIsUpdatingWorkflow(false);
    }
  };

  return (
    <>
      <Card className="border-none shadow-none">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-6">
            <CheckCircle2 className="h-16 w-16 text-green-500" />
          </div>
          <CardTitle className="text-2xl font-bold">Parabéns! Configuração Concluída</CardTitle>
          <CardDescription className="text-lg mt-2">
            Agora você está pronto para começar a usar o ZapCore.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-center text-center relative">
          <div className="bg-blue-50 border border-blue-100 p-4 rounded-lg text-blue-800 mb-4">
            <div className="flex items-start">
              <div className="flex-shrink-0 pt-0.5">
                <svg width="20" height="20" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-blue-500"><path d="M7.5 1.5C7.5 1.22386 7.27614 1 7 1C6.72386 1 6.5 1.22386 6.5 1.5V7.5C6.5 7.63807 6.55268 7.76307 6.64645 7.85685L9.14645 10.3569C9.34171 10.5521 9.65829 10.5521 9.85355 10.3569C10.0488 10.1616 10.0488 9.84502 9.85355 9.64976L7.5 7.29621V1.5Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path><path d="M7.5 15C11.6421 15 15 11.6421 15 7.5C15 3.35786 11.6421 0 7.5 0C3.35786 0 0 3.35786 0 7.5C0 11.6421 3.35786 15 7.5 15ZM7.5 14C11.0899 14 14 11.0899 14 7.5C14 3.91015 11.0899 1 7.5 1C3.91015 1 1 3.91015 1 7.5C1 11.0899 3.91015 14 7.5 14Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path></svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-left">
                  <span className="font-medium">Último passo:</span> Finalize sua configuração para transferir seus dados para o workflow do n8n e ativar seu assistente personalizado.
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex flex-col items-center pt-4">
            <Button 
              size="lg" 
              onClick={handleOpenWorkflowDialog}
              className="px-6 py-3 text-lg w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white shadow-md hover:shadow-lg transition-all"
            >
              <svg width="24" height="24" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" className="mr-2"><path d="M7.49991 0.877045C3.84222 0.877045 0.877045 3.84222 0.877045 7.49991C0.877045 11.1576 3.84222 14.1228 7.49991 14.1228C11.1576 14.1228 14.1228 11.1576 14.1228 7.49991C14.1228 3.84222 11.1576 0.877045 7.49991 0.877045ZM7.49988 1.82704C4.36686 1.82704 1.82704 4.36686 1.82704 7.49988C1.82704 10.6329 4.36686 13.1727 7.49988 13.1727C10.6329 13.1727 13.1727 10.6329 13.1727 7.49988C13.1727 4.36686 10.6329 1.82704 7.49988 1.82704ZM10.1589 5.53774C10.3178 5.31191 10.2636 5.00001 10.0378 4.84109C9.81193 4.68217 9.50003 4.73642 9.34111 4.96225L6.51977 8.97154L5.35681 7.78706C5.16334 7.59002 4.84677 7.58711 4.64973 7.78058C4.45268 7.97404 4.44978 8.29061 4.64325 8.48765L6.22658 10.1003C6.33054 10.2062 6.47617 10.2604 6.62407 10.2483C6.77197 10.2363 6.90686 10.1591 6.99427 10.0377L10.1589 5.53774Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path></svg>
              Finalizar Configuração
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Finalizar Configuração</DialogTitle>
            <DialogDescription>
              Este é o último passo para ativar seu assistente personalizado no n8n. Selecione o workflow para transferir seus dados de configuração.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {isLoadingWorkflows ? (
              <div className="flex justify-center items-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <span className="ml-2">Carregando workflows...</span>
              </div>
            ) : errorMessage && workflows.length === 0 ? (
              <div className="bg-red-50 p-4 rounded text-red-800 text-sm">
                {errorMessage}
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <label htmlFor="workflow" className="text-sm font-medium">
                    Workflow
                  </label>
                  <Select 
                    value={selectedWorkflowId} 
                    onValueChange={setSelectedWorkflowId}
                  >
                    <SelectTrigger id="workflow">
                      <SelectValue placeholder="Selecione um workflow" />
                    </SelectTrigger>
                    <SelectContent>
                      {workflows.map(workflow => (
                        <SelectItem key={workflow.id} value={String(workflow.id)}>
                          {workflow.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {errorMessage && (
                  <div className="bg-red-50 p-4 rounded text-red-800 text-sm">
                    {errorMessage}
                  </div>
                )}
              </>
            )}
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCloseWorkflowDialog}
              disabled={isUpdatingWorkflow}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleUpdateWorkflow}
              disabled={isLoadingWorkflows || isUpdatingWorkflow || !selectedWorkflowId}
              className="bg-green-600 hover:bg-green-700"
            >
              {isUpdatingWorkflow ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Finalizando...
                </>
              ) : (
                <>Finalizar Configuração</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}; 